import {
  Bookmark,
  BookmarkCheck,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  ExternalLink,
  GitBranch,
  ImageIcon,
  RotateCcw,
  X,
  ZoomIn,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  CLIENT_COMMS_EVENTS,
  CLIENT_COMMS_PROPERTIES,
} from '@/constants/analytics/client-communications-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

interface SmsMessageBubbleProps {
  message: UnifiedCommunication;
  showTimestamp?: boolean;
}

export const SmsMessageBubble: React.FC<SmsMessageBubbleProps> = ({
  message,
  showTimestamp = true,
}) => {
  const {
    matterId,
    markAsRead,
    isRead,
    toggleBookmark,
    isBookmarked,
    retryPendingMessage,
  } = useClientCommunications();
  const capture = usePostHogCapture();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showRelatedMatters, setShowRelatedMatters] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<{
    src: string;
    alt: string;
    downloadUrl: string;
    filename: string;
  } | null>(null);

  const isInbound = message.direction === 'inbound';
  const read = isRead(message.id);
  const bookmarked = isBookmarked(message.id);

  // Mark as read when rendered (for inbound messages)
  React.useEffect(() => {
    if (isInbound && !read) {
      markAsRead(message.id);
    }
  }, [isInbound, read, message.id, markAsRead]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatFullDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleCopyMessage = useCallback(() => {
    navigator.clipboard.writeText(message.content);
    toast.success('Message copied');
    setIsMenuOpen(false);
  }, [message.content]);

  const handleToggleBookmark = useCallback(() => {
    toggleBookmark(message.id);
    toast.success(bookmarked ? 'Bookmark removed' : 'Message bookmarked');
    setIsMenuOpen(false);
  }, [message.id, toggleBookmark, bookmarked]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    // Track retry clicked
    capture(CLIENT_COMMS_EVENTS.SMS_RETRY_CLICKED, {
      [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
      [CLIENT_COMMS_PROPERTIES.COMMUNICATION_ID]: message.id,
      [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
      [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
    });
    try {
      await retryPendingMessage(message.id);
    } finally {
      setIsRetrying(false);
    }
  }, [message.id, retryPendingMessage, matterId, capture]);

  const renderStatusIcon = () => {
    if (isInbound) return null;

    const status = message.transmission_status;
    const iconClass = 'h-3 w-3';
    // Pending messages have IDs like "pending-1234567890"
    const isPendingMessage = message.id.startsWith('pending-');

    switch (status) {
      case 'delivered':
      case 'sent':
        // Pending messages with 'sent' status show single checkmark (sent to Dialpad, awaiting webhook)
        if (isPendingMessage) {
          return <Check className={cn(iconClass, 'text-zinc-400')} />;
        }
        // Real message confirmed via webhook - show double checkmarks (blue)
        return <CheckCheck className={cn(iconClass, 'text-blue-400')} />;
      case 'sending':
      case 'pending':
        return (
          <Clock className={cn(iconClass, 'text-zinc-400 animate-pulse')} />
        );
      case 'failed':
      case 'bounced':
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRetry();
            }}
            disabled={isRetrying}
            className="flex items-center gap-1 text-[10px] text-red-500 font-medium hover:text-red-600 transition-colors disabled:opacity-50"
            title={message.transmission_error || 'Click to retry'}
          >
            {isRetrying ? (
              <Clock className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3" />
            )}
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>
        );
      default:
        // Any other real message status - show double checkmarks (gray for unknown status)
        if (!isPendingMessage) {
          return <CheckCheck className={cn(iconClass, 'text-zinc-400')} />;
        }
        return null;
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col max-w-[75%]',
        isInbound ? 'items-start' : 'items-end',
      )}
    >
      <Dialog
        open={!!enlargedImage}
        onOpenChange={(open) => !open && setEnlargedImage(null)}
      >
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none"
          hideCloseButton
        >
          {enlargedImage && (
            <div className="relative flex items-center justify-center">
              <img
                src={enlargedImage.src}
                alt={enlargedImage.alt}
                className="max-w-[85vw] max-h-[85vh] object-contain"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <a
                  href={enlargedImage.downloadUrl}
                  download={enlargedImage.filename}
                  className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                  title="Download image"
                >
                  <Download className="h-5 w-5" />
                </a>
                <button
                  type="button"
                  onClick={() => setEnlargedImage(null)}
                  className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'px-3 py-2 rounded-2xl text-[13px] leading-relaxed font-medium text-left',
              'cursor-pointer transition-all duration-150',
              isInbound
                ? 'bg-zinc-200 text-zinc-900 rounded-bl-sm hover:bg-zinc-300'
                : 'bg-primary text-white rounded-br-sm hover:bg-primary/90',
              // Pending/sending state - slightly transparent
              message.transmission_status === 'pending' && 'opacity-70',
              message.transmission_status === 'failed' &&
                'bg-red-500/80 ring-1 ring-red-500 hover:bg-red-500/90',
              // Bookmarked indicator
              bookmarked && 'ring-2 ring-amber-400/50',
              // If only images (no text), reduce padding
              message.has_attachments && !message.content && 'p-1',
            )}
          >
            {/* MMS Image Attachments */}
            {message.has_attachments && message.attachments.length > 0 && (
              <div
                className={cn('flex flex-col gap-1', message.content && 'mb-2')}
              >
                {message.attachments
                  .filter((att) => att.content_type?.startsWith('image/'))
                  .map((attachment) => (
                    <div key={attachment.id} className="relative group">
                      <img
                        src={
                          attachment.thumbnail_url || attachment.download_url
                        }
                        alt={attachment.filename || 'MMS image'}
                        className="rounded-xl max-w-[200px] max-h-[200px] object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      {/* Fallback for broken images */}
                      <div
                        className={cn(
                          'hidden items-center justify-center gap-1 p-4 rounded-xl',
                          isInbound ? 'bg-zinc-300' : 'bg-primary/70',
                        )}
                      >
                        <ImageIcon className="h-5 w-5 opacity-50" />
                        <span className="text-xs opacity-70">Image</span>
                      </div>
                      {/* Clickable overlay to enlarge image */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEnlargedImage({
                            src: attachment.download_url,
                            alt: attachment.filename || 'MMS image',
                            downloadUrl: attachment.download_url,
                            filename: attachment.filename || 'image',
                          });
                        }}
                        className="absolute inset-0 cursor-pointer"
                        aria-label={`Enlarge ${attachment.filename || 'MMS image'}`}
                      />
                      {/* Enlarge icon on hover */}
                      <div
                        className={cn(
                          'absolute bottom-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none',
                          isInbound
                            ? 'bg-zinc-800/70 text-white'
                            : 'bg-white/70 text-zinc-800',
                        )}
                      >
                        <ZoomIn className="h-3 w-3" />
                      </div>
                      {/* Download button on hover */}
                      <a
                        href={attachment.download_url}
                        download={attachment.filename}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(
                          'absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity',
                          isInbound
                            ? 'bg-zinc-800/70 text-white'
                            : 'bg-white/70 text-zinc-800',
                        )}
                        title="Download image"
                      >
                        <Download className="h-3 w-3" />
                      </a>
                    </div>
                  ))}
                {/* Non-image attachments (show as file indicator) */}
                {message.attachments
                  .filter((att) => !att.content_type?.startsWith('image/'))
                  .map((attachment) => (
                    <a
                      key={attachment.id}
                      href={attachment.download_url}
                      download={attachment.filename}
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        'flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs',
                        isInbound
                          ? 'bg-zinc-300 text-zinc-700 hover:bg-zinc-400'
                          : 'bg-white/20 text-white hover:bg-white/30',
                      )}
                    >
                      <Download className="h-3 w-3" />
                      <span className="truncate max-w-[150px]">
                        {attachment.filename || 'Attachment'}
                      </span>
                    </a>
                  ))}
              </div>
            )}
            {/* Text content */}
            {message.content && (
              <p className="whitespace-pre-wrap break-words">
                {message.content}
              </p>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={isInbound ? 'right' : 'left'}
          align="start"
          className="w-auto p-1"
        >
          <div className="flex flex-col gap-0.5">
            {/* Timestamp */}
            <div className="px-2 py-1.5 text-[11px] text-zinc-500 font-medium border-b border-zinc-100 mb-0.5">
              {formatFullDateTime(message.timestamp)}
            </div>

            {/* Copy */}
            <button
              type="button"
              onClick={handleCopyMessage}
              className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 rounded transition-colors"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy message
            </button>

            {/* Bookmark */}
            <button
              type="button"
              onClick={handleToggleBookmark}
              className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 rounded transition-colors"
            >
              {bookmarked ? (
                <>
                  <BookmarkCheck className="h-3.5 w-3.5 text-amber-500" />
                  Remove bookmark
                </>
              ) : (
                <>
                  <Bookmark className="h-3.5 w-3.5" />
                  Bookmark
                </>
              )}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Cross-matter indicator */}
      {message.related_matter_count != null &&
        message.related_matter_count > 0 &&
        message.related_matters &&
        message.related_matters.length > 0 && (
          <div className={cn('flex flex-col mt-0.5', isInbound ? 'self-start' : 'self-end')}>
            <button
              type="button"
              onClick={() => setShowRelatedMatters((prev) => !prev)}
              className={cn(
                'flex items-center gap-1 px-1.5 py-0.5 rounded-md',
                'text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200',
                'hover:bg-gray-200 transition-colors',
              )}
            >
              <GitBranch className="h-3 w-3 text-gray-600" />
              <span>
                Also on {message.related_matter_count} other{' '}
                {message.related_matter_count === 1 ? 'matter' : 'matters'}
              </span>
              {showRelatedMatters ? (
                <ChevronUp className="h-3 w-3 text-gray-600" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-600" />
              )}
            </button>
            {showRelatedMatters && (
              <ul className="mt-1 space-y-0.5">
                {message.related_matters.map((rm) => (
                  <li key={rm.matter_id}>
                    <a
                      href={`/matters/${rm.matter_id}/client-comms`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {rm.matter_name || rm.matter_id}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

      {showTimestamp && (
        <div
          className={cn(
            'flex items-center gap-1 mt-0.5 px-1',
            isInbound ? 'flex-row' : 'flex-row-reverse',
          )}
        >
          <span className="text-xs text-gray-600 font-medium leading-tight">
            {formatTime(message.timestamp)}
          </span>
          {renderStatusIcon()}
        </div>
      )}
    </div>
  );
};

export default SmsMessageBubble;
